import { useState, useCallback } from 'react';
import { useAntiSpam } from './useAntiSpam';
import { commonConversions, ConversionType } from '../utils/fractionData';

interface Question {
  question: string;
  answer: string;
  decimal: number;
  type: ConversionType;
}

interface GameStats {
  viewed: number;
  skipped: number;
  total: number;
}

export const useFractionGameLogic = () => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [stats, setStats] = useState<GameStats>({
    viewed: 0,
    skipped: 0,
    total: 0,
  });

  const { checkAction, isBlocked } = useAntiSpam({
    minDelay: 300,
    burstLimit: 5,
    burstWindow: 3000,
    cooldownTime: 1500,
  });

  const generateQuestion = useCallback((): Question => {
    const conversion = commonConversions[Math.floor(Math.random() * commonConversions.length)];
    const type: ConversionType = Math.random() < 0.5 ? 'fraction-to-percentage' : 'percentage-to-fraction';
    
    return {
      question: type === 'fraction-to-percentage' ? conversion.fraction : conversion.percentage.toString(),
      answer: type === 'fraction-to-percentage' ? `${conversion.percentage}%` : conversion.fraction,
      decimal: conversion.decimal,
      type,
    };
  }, []);

  const addNewQuestion = useCallback(() => {
    setQuestions(prev => [...prev, generateQuestion()]);
  }, [generateQuestion]);

  const skipQuestion = () => {
    if (!checkAction()) return;

    setStats(prev => ({ ...prev, skipped: prev.skipped + 1, total: prev.total + 1 }));
    setShowAnswer(false);
    if (currentIndex === questions.length - 1) {
      addNewQuestion();
    }
    setCurrentIndex(prev => prev + 1);
  };

  const previousQuestion = () => {
    if (!checkAction()) return;

    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      setShowAnswer(false);
    }
  };

  const nextQuestion = () => {
    if (!checkAction()) return;

    if (!showAnswer) {
      setShowAnswer(true);
      setStats(prev => ({ ...prev, viewed: prev.viewed + 1, total: prev.total + 1 }));
    } else {
      setShowAnswer(false);
      if (currentIndex === questions.length - 1) {
        addNewQuestion();
      }
      setCurrentIndex(prev => prev + 1);
    }
  };

  // Initialize first question if needed
  if (questions.length === 0) {
    const firstQuestion = generateQuestion();
    setQuestions([firstQuestion]);
  }

  return {
    currentQuestion: questions[currentIndex] || generateQuestion(),
    showAnswer,
    skipQuestion,
    previousQuestion,
    nextQuestion,
    stats,
    isBlocked,
  };
};